
import { PAGE_TYPE, PageBasicProps } from 'interfaces';
import Head from 'next/head';
import { ReactElement, useEffect, useState } from 'react';
import { InView } from 'react-intersection-observer';
import styled from 'styled-components';
import Footer_v2 from '@/components/blocks/Footer/index';
import Footer from '@/components/blocks/Footer.jsx';
import Navbar_v2 from '@/components/blocks/Navbar/index';
import Navbar from '@/components/blocks/Navbar.jsx';
import { FooterProps, NavbarProps } from '@/queries/components/blocks';

const MainContainer = styled.main<{ backgroundColor?: string }>`
	background-color: ${({ backgroundColor }) => backgroundColor};
`;
// #region COMPONENT
interface PageWrapperProps {
	/**
	 * Defines alternate URLs when they exist.
	 */
	readonly alternateUrl?: {
		readonly en?: string;
		readonly es?: string;
		readonly fr?: string;
	};
	/**
	 * Allow to customize entire page background color.
	 */
	readonly backgroundColor?: string;
	/**
	 * Any React children.
	 */
	readonly children: ReactElement | ReactElement[];
	/**
	 * Current language of the website, choosen by the user.
	 */
	readonly currentLang: PageBasicProps['currentLang'];
	/**
	 * Current URL path of the user's visited page.
	 */
	readonly currentPath: PageBasicProps['currentPath'];
	/**
	 * Defines the meta description of the page.
	 */
	readonly description: string;
	/**
	 * Defines the og:image, most of the time this is the introSection image.
	 */
	readonly introImageUrl?: string;
	/**
	 * Allows to hide the default navbar for special landing pages.
	 */
	readonly isNavbarHidden?: boolean;
	/**
	 * Allows to define on which version the page is (v1 = old Next.js website, v2 = new redesigned website).
	 */
	readonly isv2?: boolean;
	/**
	 * Allows to define the type of the page for dynamic content (B2B or B2C).
	 */
	readonly pageType: PAGE_TYPE;
	/**
	 * Defines the title and meta title of the page.
	 */
	readonly title: string;
	/**
	 * Returned footer data defined from Strapi CMS.
	 */
	readonly footerData?: FooterProps;
	/**
	 * Returned navbar data defined from Strapi CMS.
	 */
	readonly navbarData?: NavbarProps;
}

const PageWrapper = ({
	title,
	children,
	description,
	introImageUrl,
	alternateUrl,
	currentPath,
	currentLang,
	isNavbarHidden,
	backgroundColor,
	isv2,
	pageType,
	footerData,
	navbarData,
}: PageWrapperProps) => {

	const [url, setUrl] = useState<string | undefined>('');
	const [loadedWindow, setLoadedWindow] = useState<Window & typeof globalThis | undefined>(undefined);

	useEffect(
		() => {
			setLoadedWindow(window);
			setUrl(loadedWindow?.location?.href);

			return () => {
				setLoadedWindow(undefined);
				setUrl(undefined);
			};
		},
		[loadedWindow?.location?.href],
	);

	const pageTitle = `Bruce • ${title}`;

	// #region RENDER
	return (
		<>
			<Head>
				<title>{pageTitle}</title>
				<meta name='description' content={description} />
				<link rel='canonical' href={url} />
				{alternateUrl?.fr !== undefined && (
					<>
						<link rel='alternate' href={`https://www.bruce.work/fr${alternateUrl.fr}`} hrefLang='x-default' />
						<link rel='alternate' href={`https://www.bruce.work/fr${alternateUrl.fr}`} hrefLang='fr' />
					</>
				)}
				{alternateUrl?.en !== undefined && <link rel='alternate' href={`https://www.bruce.work/en${alternateUrl.en}`} hrefLang='en' />}
				{alternateUrl?.es !== undefined && <link rel='alternate' href={`https://www.bruce.work/es${alternateUrl.es}`} hrefLang='es' />}
				<meta name='theme-color' content='#fff' />
				<meta name='viewport' content='width=device-width, initial-scale=1' />
				<meta property='og:type' content='website' />
				<meta property='og:title' content={`Bruce • ${title}`} />
				<meta property='og:description' content={description} />
				<meta property='og:image' content={`https://www.bruce.work${introImageUrl}`} />
				<meta property='og:image:url' content={`https://www.bruce.work${introImageUrl}`} />
				<meta property='og:url' content={url} />
				<meta property='og:site_name' content='Bruce'/>
			</Head>
			{
				isNavbarHidden
					? (
						null
					) : isv2 && navbarData
						? (
							<Navbar_v2 navbarData={navbarData} />
						) : (
							<Navbar currentLang={currentLang} isTriggerVisible={false} />
						)
			}
			<MainContainer backgroundColor={backgroundColor}>
				{children}
			</MainContainer>
			{isv2 && footerData ? (
				<InView triggerOnce rootMargin='400px'>
					{({ inView, ref }) => (
						<div ref={ref}>
							{inView &&
								<Footer_v2
									footerData={footerData}
									pageType={pageType}
								/>
							}
						</div>
					)}
				</InView>
			) : (
				<Footer currentPath={currentPath} />
			)}
		</>
	);
	// #endregion
};

export default PageWrapper;
// #endregion
